// src/services/requestsService.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export const requestsService = {
  
  // ==================== PROVIDER PROFILE METHODS ====================
  
  async createProviderProfile(bookId, userId, profileData) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      
      const providerData = {
        providerId: userId,
        bookId: bookId,
        businessName: profileData.businessName,
        categories: profileData.categories || [],
        description: profileData.description || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        website: profileData.website || '',
        logo: profileData.logo || '',
        verified: profileData.verified || false,
        featured: profileData.featured || false,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...Object.keys(profileData)
          .filter(key => !['businessName', 'categories', 'description', 'email', 'phone', 'website', 'logo', 'verified', 'featured'].includes(key))
          .reduce((obj, key) => {
            obj[key] = profileData[key];
            return obj;
          }, {})
      };

      const docRef = await addDoc(providersRef, providerData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating provider profile:', error);
      throw error;
    }
  },

  async getProviderProfiles(bookId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(providersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const providers = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      
      return providers.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        if (a.verified && !b.verified) return -1;
        if (!a.verified && b.verified) return 1;
        return 0;
      });
    } catch (error) {
      console.error('Error getting provider profiles:', error);
      return [];
    }
  },

  async getProviderProfile(bookId, providerId) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', providerId);
      const docSnap = await getDoc(providerRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting provider profile:', error);
      return null;
    }
  },

  async getProviderProfileByUserId(bookId, userId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(providersRef, where('providerUserId', '==', userId));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting provider profile by user ID:', error);
      return null;
    }
  },

  async updateProviderProfile(bookId, providerId, updates) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', providerId);
      await updateDoc(providerRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating provider profile:', error);
      throw error;
    }
  },

  async deleteProviderProfile(bookId, providerId) {
    try {
      const providerRef = doc(db, 'pb-books', bookId, 'pb-providers', providerId);
      await deleteDoc(providerRef);
    } catch (error) {
      console.error('Error deleting provider profile:', error);
      throw error;
    }
  },

  async getProvidersByCategory(bookId, category) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(
        providersRef, 
        where('categories', 'array-contains', category),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting providers by category:', error);
      return [];
    }
  },

  async getVerifiedProviders(bookId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(
        providersRef, 
        where('verified', '==', true),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting verified providers:', error);
      return [];
    }
  },

  async getFeaturedProviders(bookId) {
    try {
      const providersRef = collection(db, 'pb-books', bookId, 'pb-providers');
      const q = query(
        providersRef, 
        where('featured', '==', true),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting featured providers:', error);
      return [];
    }
  },

  // ==================== REQUEST METHODS ====================

  async createRequest(bookId, userId, requestData) {
    try {
      const requestsRef = collection(db, 'pb-books', bookId, 'pb-requests');
      
      const request = {
        bookId: bookId,
        userId: userId || null,
        title: requestData.title || 'Untitled Request',
        description: requestData.description,
        category: requestData.category || 'general',
        budget: requestData.budget || null,
        timeframe: requestData.timeframe || '',
        location: requestData.location || '',
        status: 'open',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...requestData
      };

      const docRef = await addDoc(requestsRef, request);
      return docRef.id;
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  },

  async getOpenRequests(bookId, category = null) {
    try {
      const requestsRef = collection(db, 'pb-books', bookId, 'pb-requests');
      let q;
      
      if (category) {
        q = query(
          requestsRef, 
          where('status', '==', 'open'),
          where('category', '==', category),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          requestsRef, 
          where('status', '==', 'open'),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting open requests:', error);
      return [];
    }
  },

  async getUserRequests(bookId, userId) {
    try {
      if (!userId) return [];

      const requestsRef = collection(db, 'pb-books', bookId, 'pb-requests');
      const q = query(
        requestsRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting user requests:', error);
      return [];
    }
  },

  async updateRequestStatus(bookId, requestId, status) {
    try {
      const requestRef = doc(db, 'pb-books', bookId, 'pb-requests', requestId);
      await updateDoc(requestRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating request status:', error);
      throw error;
    }
  },

  // ==================== OFFER METHODS ====================

  async createOffer(bookId, requestId, providerId, offerData) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      
      const offer = {
        bookId: bookId,
        requestId: requestId,
        providerId: providerId,
        providerName: offerData.providerName,
        providerCategories: offerData.providerCategories || [],
        valueProp: offerData.valueProp,
        price: offerData.price,
        timeframe: offerData.timeframe,
        status: offerData.status || 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...offerData
      };

      const docRef = await addDoc(offersRef, offer);
      return docRef.id;
    } catch (error) {
      console.error('Error creating offer:', error);
      throw error;
    }
  },

  async getOffersForRequest(bookId, requestId) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      const q = query(
        offersRef, 
        where('requestId', '==', requestId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting offers for request:', error);
      return [];
    }
  },

  async getOffersByProvider(bookId, providerId) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      const q = query(
        offersRef, 
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting offers by provider:', error);
      return [];
    }
  },

  async getAllOffers(bookId) {
    try {
      const offersRef = collection(db, 'pb-books', bookId, 'pb-offers');
      const q = query(offersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting all offers:', error);
      return [];
    }
  },

  async updateOfferStatus(bookId, offerId, status) {
    try {
      const offerRef = doc(db, 'pb-books', bookId, 'pb-offers', offerId);
      await updateDoc(offerRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating offer status:', error);
      throw error;
    }
  },

  // ==================== PRESET OFFERS METHODS ====================

  async createPresetOffer(bookId, providerId, presetData) {
    try {
      const presetsRef = collection(db, 'pb-books', bookId, 'pb-preset-offers');
      
      const preset = {
        providerId: providerId,
        bookId: bookId,
        name: presetData.name,
        category: presetData.category,
        valueProp: presetData.valueProp,
        price: presetData.price,
        timeframe: presetData.timeframe,
        description: presetData.description || '',
        autoRespond: presetData.autoRespond || false,
        active: presetData.active !== false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(presetsRef, preset);
      return docRef.id;
    } catch (error) {
      console.error('Error creating preset offer:', error);
      throw error;
    }
  },

  async getProviderPresets(bookId, providerId) {
    try {
      const presetsRef = collection(db, 'pb-books', bookId, 'pb-preset-offers');
      const q = query(
        presetsRef, 
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting provider presets:', error);
      return [];
    }
  },

  async getAutoRespondPresets(bookId, category) {
    try {
      const presetsRef = collection(db, 'pb-books', bookId, 'pb-preset-offers');
      const q = query(
        presetsRef, 
        where('category', '==', category),
        where('autoRespond', '==', true),
        where('active', '==', true)
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting auto-respond presets:', error);
      return [];
    }
  },

  async updatePresetOffer(bookId, presetId, updates) {
    try {
      const presetRef = doc(db, 'pb-books', bookId, 'pb-preset-offers', presetId);
      await updateDoc(presetRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating preset offer:', error);
      throw error;
    }
  },

  async deletePresetOffer(bookId, presetId) {
    try {
      const presetRef = doc(db, 'pb-books', bookId, 'pb-preset-offers', presetId);
      await deleteDoc(presetRef);
    } catch (error) {
      console.error('Error deleting preset offer:', error);
      throw error;
    }
  },

  async usePresetOffer(bookId, requestId, providerId, presetId) {
    try {
      const presetRef = doc(db, 'pb-books', bookId, 'pb-preset-offers', presetId);
      const presetSnap = await getDoc(presetRef);

      if (!presetSnap.exists()) {
        throw new Error('Preset not found');
      }

      const preset = { id: presetSnap.id, ...presetSnap.data() };

      const provider = await this.getProviderProfile(bookId, providerId);

      return await this.createOffer(bookId, requestId, providerId, {
        providerName: provider?.businessName || 'Provider',
        providerCategories: provider?.categories || [],
        valueProp: preset.valueProp,
        price: preset.price,
        timeframe: preset.timeframe,
        status: 'pending',
        presetId: presetId,
        presetName: preset.name
      });
    } catch (error) {
      console.error('Error using preset offer:', error);
      throw error;
    }
  },

  // ==================== STATISTICS ====================

  async getStats(bookId) {
    try {
      const [requestsSnap, offersSnap, providersSnap] = await Promise.all([
        getDocs(collection(db, 'pb-books', bookId, 'pb-requests')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-offers')),
        getDocs(collection(db, 'pb-books', bookId, 'pb-providers'))
      ]);

      const requests = requestsSnap.docs.map(d => d.data());
      const offers = offersSnap.docs.map(d => d.data());
      const providers = providersSnap.docs.map(d => d.data());

      return {
        totalRequests: requests.length,
        openRequests: requests.filter(r => r.status === 'open').length,
        totalOffers: offers.length,
        pendingOffers: offers.filter(o => o.status === 'pending').length,
        totalProviders: providers.length,
        verifiedProviders: providers.filter(p => p.verified).length,
        featuredProviders: providers.filter(p => p.featured).length
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return {
        totalRequests: 0,
        openRequests: 0,
        totalOffers: 0,
        pendingOffers: 0,
        totalProviders: 0,
        verifiedProviders: 0,
        featuredProviders: 0
      };
    }
  },

  async generateAIOffers(bookId, requestId, requestData) {
    try {
      console.log('AI offer generation not yet implemented');
      return [];
    } catch (error) {
      console.error('Error generating AI offers:', error);
      return [];
    }
  }
};